import React, { useState, useMemo } from 'react';
import { Box, Typography, Avatar, TextField, Checkbox, FormControlLabel, Paper, MenuItem, FormControl, InputLabel, Select, Button, Alert } from '@mui/material';
import { useUser } from '../contexts/UserContext';

// Mock user data for fallback
const mockUser = {
  username: 'johndoe',
  title: 'Mr.',
  firstName: 'John',
  lastName: 'Doe',
  highSchool: 'Fosterway High School',
  avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
  email: 'johndoe@centralhs.edu',
  shareEmail: true,
  isAdmin: false,
};

const UserProfile: React.FC = () => {
  const { user, loading, error } = useUser();
  
  const [title, setTitle] = useState(mockUser.title);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [shareEmail, setShareEmail] = useState(mockUser.shareEmail);
  const [isAdmin, setIsAdmin] = useState(mockUser.isAdmin);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [lastSaved, setLastSaved] = useState({
    title: mockUser.title,
    firstName: mockUser.firstName,
    lastName: mockUser.lastName,
    shareEmail: mockUser.shareEmail,
    isAdmin: mockUser.isAdmin,
  });

  // Determine if there are unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    return (
      title !== lastSaved.title ||
      firstName !== lastSaved.firstName ||
      lastName !== lastSaved.lastName ||
      shareEmail !== lastSaved.shareEmail ||
      isAdmin !== lastSaved.isAdmin
    );
  }, [title, firstName, lastName, shareEmail, isAdmin, lastSaved]);

  const handleSave = () => {
    // Save logic here
    setLastSaved({ title, firstName, lastName, shareEmail, isAdmin });
    setShowConfirmation(true);
    setTimeout(() => setShowConfirmation(false), 3000);
  };

  if (loading) {
    return (
      <Box sx={{ maxWidth: 500, mx: 'auto', mt: 6 }}>
        <Paper sx={{ p: 4 }}>
          <Typography>Loading user profile...</Typography>
        </Paper>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 500, mx: 'auto', mt: 6 }}>
        <Paper sx={{ p: 4 }}>
          <Alert severity="error">Error loading profile: {error}</Alert>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 500, mx: 'auto', mt: 6 }}>
      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <Avatar 
            src={user?.userIcon || ''} 
            alt={`${firstName || user?.username || ''} ${lastName || ''}`} 
            sx={{ width: 96, height: 96, mb: 2 }} 
          />
          <Typography variant="h5" gutterBottom>
            {firstName || user?.username || ''} {lastName || ''}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            {user?.schoolName || ''}
          </Typography>
          {user?.city && user?.province && (
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              {user.city}, {user.province}
            </Typography>
          )}
        </Box>
        <FormControl fullWidth margin="normal">
          <InputLabel id="title-label">Title</InputLabel>
          <Select
            labelId="title-label"
            value={title}
            label="Title"
            onChange={(e) => setTitle(e.target.value)}
          >
            <MenuItem value="">&nbsp;</MenuItem>
            <MenuItem value="Mr.">Mr.</MenuItem>
            <MenuItem value="Ms.">Ms.</MenuItem>
            <MenuItem value="Mrs.">Mrs.</MenuItem>
            <MenuItem value="Miss">Miss</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="First Name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          fullWidth
          margin="normal"
        />
        <TextField
          label="Last Name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          fullWidth
          margin="normal"
        />
        <TextField
          label="School Email"
          value={user?.email || ''}
          fullWidth
          margin="normal"
          InputProps={{ readOnly: true }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={shareEmail}
              onChange={(e) => setShareEmail(e.target.checked)}
              color="primary"
            />
          }
          label="OK to share contact information with colleges & universities"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={isAdmin}
              onChange={(e) => setIsAdmin(e.target.checked)}
              color="primary"
            />
          }
          label="Admin user"
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button variant="contained" color="primary" disabled={!hasUnsavedChanges} onClick={handleSave}>
            Save
          </Button>
        </Box>
        {showConfirmation && (
          <Alert severity="success" sx={{ mt: 3 }}>
            Profile information saved successfully.
          </Alert>
        )}
      </Paper>
    </Box>
  );
};

export default UserProfile; 